// ════════════════════════════════════════════════════════════
// Facturas emitidas — listado y editor
// ════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Grid, Chip, Alert,
  TextField, IconButton, Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment,
  Autocomplete, Pagination, CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  getConfigERP, getFacturas, getFactura, crearFactura, actualizarFactura,
  anularFactura, getClientes, descargarFacturaPDF
} from '../api';

import Proformas from '../components/Proformas';

const eur = (n: number) =>
  (n || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });

const LINEA_VACIA = {
  concepto: '', descripcion: '', cantidad: 1, precio_unitario: 0,
  descuento: 0, tipo_iva: 21
};

export default function Facturas() {
  const [config, setConfig] = useState<any>(null);
  const [clientes, setClientes] = useState<any[]>([]);
  const [datos, setDatos] = useState<any>({ items: [], total: 0, pages: 1, suma_total: 0 });
  const [filtros, setFiltros] = useState({ search: '', estado: '', desde: '', hasta: '' });
  const [pagina, setPagina] = useState(1);
  const [editor, setEditor] = useState<any>(null);
  const [guardando, setGuardando] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [tabPrincipal, setTabPrincipal] = useState(0);

  const cargar = () => {
    getFacturas({ ...filtros, page: pagina, limit: 25 })
      .then(setDatos).finally(() => setCargando(false));
  };

  useEffect(() => {
    getConfigERP().then(setConfig).catch(() => {});
    getClientes().then(setClientes).catch(() => setClientes([]));
  }, []);
  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [pagina, filtros]);

  // ── Editor ──
  const nuevaFactura = () => setEditor({
    id: null, serie: 'FV', fecha: new Date().toISOString().slice(0, 10),
    cliente_id: null, forma_pago: 'contado', observaciones: '',
    regimen_operacion: 'nacional', lineas: [{ ...LINEA_VACIA }]
  });

  const abrirFactura = async (id: number) => {
    const f = await getFactura(id);
    setEditor({ ...f, lineas: f.lineas.length ? f.lineas : [{ ...LINEA_VACIA }] });
  };

  const setLinea = (i: number, campo: string, valor: any) => {
    const lineas = [...editor.lineas];
    lineas[i] = { ...lineas[i], [campo]: valor };
    setEditor({ ...editor, lineas });
  };

  const totales = () => {
    let base = 0, cuota = 0;
    (editor?.lineas || []).forEach((l: any) => {
      const b = (parseFloat(l.cantidad) || 0) * (parseFloat(l.precio_unitario) || 0)
        * (1 - (parseFloat(l.descuento) || 0) / 100);
      base += b;
      cuota += b * (parseFloat(l.tipo_iva) || 0) / 100;
    });
    return { base: Math.round(base * 100) / 100, cuota: Math.round(cuota * 100) / 100,
             total: Math.round((base + cuota) * 100) / 100 };
  };

  const guardar = async () => {
    if (!editor.cliente_id) { alert('Selecciona el cliente'); return; }
    if (!editor.lineas.some((l: any) => l.concepto?.trim())) {
      alert('Añade al menos una línea con concepto'); return;
    }
    setGuardando(true);
    try {
      const payload = {
        ...editor,
        lineas: editor.lineas.filter((l: any) => l.concepto?.trim()),
      };
      if (editor.id) {
        await actualizarFactura(editor.id, payload);
      } else {
        const r = await crearFactura(payload);
        alert(`✅ Factura ${r.numero} creada`);
      }
      setEditor(null);
      cargar();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al guardar la factura');
    } finally {
      setGuardando(false);
    }
  };

  const anular = async (f: any) => {
    const motivo = window.prompt(`Anular la factura ${f.numero}. Motivo:`);
    if (!motivo) return;
    try { await anularFactura(f.id, motivo); cargar(); }
    catch (e: any) { alert(e.response?.data?.detail || 'Error'); }
  };

  // ════════ EDITOR ════════
  if (editor) {
    const t = totales();
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Button onClick={() => setEditor(null)}>← Volver</Button>
          <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>
            {editor.numero ? `Factura ${editor.numero}` : 'Nueva factura'}
          </Typography>
          {editor.id && (
            <Button variant="outlined" onClick={() => descargarFacturaPDF(editor.id, editor.numero)}
              sx={{ borderColor: '#F5C200', color: '#a07c00' }}>
              📄 Descargar PDF
            </Button>
          )}
          <Button variant="contained" onClick={guardar} disabled={guardando}
            sx={{ bgcolor: '#4f46e5' }}>
            {guardando ? 'Guardando...' : (editor.id ? 'Guardar cambios' : 'Emitir factura')}
          </Button>
        </Box>

        <Card sx={{ borderRadius: 3, mb: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={clientes}
                  getOptionLabel={(c: any) => c.nombre || ''}
                  value={clientes.find((c: any) => c.id === editor.cliente_id) || null}
                  onChange={(_, v: any) => setEditor({ ...editor, cliente_id: v?.id || null,
                    forma_pago: v?.forma_pago || editor.forma_pago })}
                  disabled={!!editor.id}
                  renderInput={(p) => <TextField {...p} size="small" label="Cliente *" />}
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Serie</InputLabel>
                  <Select value={editor.serie} label="Serie" disabled={!!editor.id}
                    onChange={(e) => setEditor({ ...editor, serie: e.target.value })}>
                    {(config?.series || []).filter((s: any) => s.tipo_documento === 'factura_emitida')
                      .map((s: any) => <MenuItem key={s.clave} value={s.clave}>{s.clave} — {s.descripcion}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField fullWidth size="small" type="date" label="Fecha"
                  InputLabelProps={{ shrink: true }} value={editor.fecha || ''}
                  onChange={(e) => setEditor({ ...editor, fecha: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Régimen fiscal</InputLabel>
                  <Select value={editor.regimen_operacion || 'nacional'} label="Régimen fiscal"
                    disabled={!!editor.id}
                    onChange={(e) => {
                      const r: any = (config?.regimenes_operacion || [])
                        .find((x: any) => x.clave === e.target.value);
                      // Al cambiar de régimen, ajusta el IVA de todas las líneas
                      const lineas = editor.lineas.map((l: any) => ({ ...l, tipo_iva: r?.iva ?? 21 }));
                      setEditor({ ...editor, regimen_operacion: e.target.value, lineas });
                    }}>
                    {(config?.regimenes_operacion || []).map((r: any) =>
                      <MenuItem key={r.clave} value={r.clave}>{r.nombre}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Forma de pago</InputLabel>
                  <Select value={editor.forma_pago || 'contado'} label="Forma de pago"
                    onChange={(e) => setEditor({ ...editor, forma_pago: e.target.value })}>
                    {(config?.formas_pago || []).map((f: any) =>
                      <MenuItem key={f.clave} value={f.clave}>{f.nombre}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Mención legal del régimen elegido */}
            {(() => {
              const r: any = (config?.regimenes_operacion || [])
                .find((x: any) => x.clave === (editor.regimen_operacion || 'nacional'));
              if (!r?.mencion) return null;
              return (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                    Esta factura irá SIN IVA y llevará impresa la siguiente mención legal:
                  </Typography>
                  <Typography variant="caption">{r.mencion}</Typography>
                  {r.modelo && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      Se declarará en el modelo {r.modelo}.
                    </Typography>
                  )}
                </Alert>
              );
            })()}
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3, mb: 2 }}>
          <CardContent>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>Líneas</Typography>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Concepto</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 90 }}>Cant.</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 120 }}>Precio</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 90 }}>Dto %</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 110 }}>IVA</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 110 }} align="right">Total</TableCell>
                    <TableCell sx={{ width: 50 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {editor.lineas.map((l: any, i: number) => {
                    const base = (parseFloat(l.cantidad) || 0) * (parseFloat(l.precio_unitario) || 0)
                      * (1 - (parseFloat(l.descuento) || 0) / 100);
                    return (
                      <TableRow key={i}>
                        <TableCell>
                          <TextField fullWidth variant="standard" placeholder="Concepto"
                            value={l.concepto || ''}
                            onChange={(e) => setLinea(i, 'concepto', e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <TextField variant="standard" type="number" value={l.cantidad}
                            onChange={(e) => setLinea(i, 'cantidad', e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <TextField variant="standard" type="number" value={l.precio_unitario}
                            onChange={(e) => setLinea(i, 'precio_unitario', e.target.value)}
                            InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }} />
                        </TableCell>
                        <TableCell>
                          <TextField variant="standard" type="number" value={l.descuento}
                            onChange={(e) => setLinea(i, 'descuento', e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <Select variant="standard" fullWidth value={l.tipo_iva}
                            onChange={(e) => setLinea(i, 'tipo_iva', e.target.value)}>
                            {(config?.tipos_iva || []).map((iva: any) =>
                              <MenuItem key={iva.clave} value={iva.porcentaje}>{iva.porcentaje}%</MenuItem>)}
                          </Select>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {eur(base * (1 + (parseFloat(l.tipo_iva) || 0) / 100))}
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" color="error"
                            onClick={() => setEditor({ ...editor,
                              lineas: editor.lineas.filter((_: any, j: number) => j !== i) })}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Button startIcon={<AddIcon />} sx={{ mt: 1 }}
              onClick={() => setEditor({ ...editor, lineas: [...editor.lineas, { ...LINEA_VACIA }] })}>
              Añadir línea
            </Button>
          </CardContent>
        </Card>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={7}>
            <TextField fullWidth multiline rows={3} size="small" label="Observaciones"
              value={editor.observaciones || ''}
              onChange={(e) => setEditor({ ...editor, observaciones: e.target.value })} />
          </Grid>
          <Grid item xs={12} sm={5}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">Base imponible</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{eur(t.base)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">IVA</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{eur(t.cuota)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1,
                  borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography sx={{ fontWeight: 700 }}>TOTAL</Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: 20 }}>{eur(t.total)}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  // ════════ LISTADO ════════
  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 2, fontSize: { xs: '1.5rem', md: '2rem' } }}>
        🧾 Facturación
      </Typography>
      <Tabs value={tabPrincipal} onChange={(_, v) => setTabPrincipal(v)} sx={{ mb: 3 }}
        variant="scrollable" scrollButtons="auto">
        <Tab label="🧾 Facturas emitidas" />
        <Tab label="📄 Proformas" />
      </Tabs>

      {tabPrincipal === 1 && <Proformas />}
      {tabPrincipal === 1 ? null : (
      <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={nuevaFactura}
          sx={{ bgcolor: '#4f46e5' }}>
          Nueva factura
        </Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth size="small" label="Buscar (nº, cliente, NIF)"
            value={filtros.search}
            onChange={(e) => { setPagina(1); setFiltros({ ...filtros, search: e.target.value }); }} />
        </Grid>
        <Grid item xs={6} sm={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Estado</InputLabel>
            <Select value={filtros.estado} label="Estado"
              onChange={(e) => { setPagina(1); setFiltros({ ...filtros, estado: e.target.value }); }}>
              <MenuItem value="">Todas</MenuItem>
              <MenuItem value="emitida">Emitidas</MenuItem>
              <MenuItem value="cobrada">Cobradas</MenuItem>
              <MenuItem value="anulada">Anuladas</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField fullWidth size="small" type="date" label="Desde"
            InputLabelProps={{ shrink: true }} value={filtros.desde}
            onChange={(e) => { setPagina(1); setFiltros({ ...filtros, desde: e.target.value }); }} />
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField fullWidth size="small" type="date" label="Hasta"
            InputLabelProps={{ shrink: true }} value={filtros.hasta}
            onChange={(e) => { setPagina(1); setFiltros({ ...filtros, hasta: e.target.value }); }} />
        </Grid>
        <Grid item xs={6} sm={2}>
          <Card sx={{ bgcolor: '#f0fdf4' }}>
            <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
              <Typography variant="caption" color="text.secondary">Total facturado</Typography>
              <Typography sx={{ fontWeight: 800 }}>{eur(datos.suma_total)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {cargando ? <CircularProgress /> : datos.items.length === 0 ? (
        <Alert severity="info">No hay facturas con esos criterios.</Alert>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Número</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Cliente</TableCell>
                  <TableCell sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>Base</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Total</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
                  <TableCell sx={{ width: 50 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {datos.items.map((f: any) => (
                  <TableRow key={f.id} hover sx={{ cursor: 'pointer' }}
                    onClick={() => abrirFactura(f.id)}>
                    <TableCell sx={{ fontWeight: 600 }}>{f.numero}</TableCell>
                    <TableCell>{f.fecha}</TableCell>
                    <TableCell>{f.cliente_nombre}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{eur(f.base_imponible)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{eur(f.total)}</TableCell>
                    <TableCell>
                      <Chip label={f.estado} size="small"
                        color={f.estado === 'cobrada' ? 'success' : f.estado === 'anulada' ? 'error' : 'warning'} />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small"
                        onClick={(e) => { e.stopPropagation(); descargarFacturaPDF(f.id, f.numero); }}>
                        📄
                      </IconButton>
                      {f.estado !== 'anulada' && (
                        <IconButton size="small" color="error"
                          onClick={(e) => { e.stopPropagation(); anular(f); }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {datos.pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination count={datos.pages} page={pagina} onChange={(_, p) => setPagina(p)} />
            </Box>
          )}
        </>
      )}
      </Box>
      )}
    </Box>
  );
}
